import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatNativeDateModule,
  DateAdapter,
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
  NativeDateAdapter,
} from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SelectionModel } from '@angular/cdk/collections';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environment/environment';

export interface CreateInvitationModalData {
  eventGuids: string[];
  events?: any[];
}

export interface EducationalPath {
  path_id: number;
  name: string;
}

export interface SearchUser {
  user_id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  educational_paths?: EducationalPath[];
  name?: string;
}

@Component({
  selector: 'app-duna-event-instructors-create-invitation-modal',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatTableModule,
    MatCheckboxModule,
    MatTooltipModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
    {
      provide: DateAdapter,
      useClass: NativeDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
  ],
  templateUrl: './create-invitation-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-invitation-modal.component.scss',
})
export class DunaEventInstructorsCreateInvitationModalComponent
  implements OnInit, OnDestroy
{
  step = 1;
  eventGuids: string[] = [];
  selectedEvents: any[] = [];

  // Confirmed invitees (chips)
  invitees: SearchUser[] = [];

  // Search state
  searchQuery = '';
  searching = false;
  searchPerformed = false;
  searchResults: SearchUser[] = [];

  // Educational path filter
  educationalPaths: EducationalPath[] = [];
  filteredPaths: EducationalPath[] = [];
  loadingPaths = false;
  selectedPathId: number | null = null;
  pathFilterControl = new FormControl<string>('');

  // Multi-select for search results
  selection = new SelectionModel<SearchUser>(true, []);

  // Deadline
  responseDeadline: Date | null = null;
  minDate = new Date();

  sending = false;
  error: string | null = null;

  readonly displayedColumns = ['select', 'name', 'email', 'paths'];

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<DunaEventInstructorsCreateInvitationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateInvitationModalData,
    private http: HttpClient
  ) {
    this.eventGuids = data?.eventGuids ?? [];
    this.selectedEvents = data?.events ?? [];

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.doSearch());
  }

  get apiUrl(): string {
    return environment.apiUrl;
  }

  ngOnInit(): void {
    this.loadEducationalPaths();
    this.pathFilterControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const q = (val ?? '').toLowerCase().trim();
        this.filteredPaths = q
          ? this.educationalPaths.filter((p) =>
              p.name.toLowerCase().includes(q)
            )
          : this.educationalPaths;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Steps ──────────────────────────────────────────────────────────────────

  nextStep(): void {
    this.step = 2;
    this.error = null;
  }

  backStep(): void {
    this.step = 1;
    this.error = null;
  }

  // ── Educational paths ──────────────────────────────────────────────────────

  private loadEducationalPaths(): void {
    this.loadingPaths = true;
    this.http
      .get<any[]>(`${this.apiUrl}/paths/list`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (paths) => {
          this.educationalPaths = (paths ?? [])
            .map((p) => {
              const shortName = (p.name ?? '').trim();
              const fullName = (p.full_name ?? '').trim();
              return { path_id: p.path_id, name: shortName || fullName };
            })
            .filter((p) => p.path_id && p.name);
          this.filteredPaths = [...this.educationalPaths];
          this.loadingPaths = false;
        },
        error: () => {
          this.loadingPaths = false;
        },
      });
  }

  onPathFilterChange(): void {
    this.selection.clear();
    this.doSearch();
  }

  onPathSelected(path: EducationalPath | null): void {
    this.selectedPathId = path?.path_id ?? null;
    this.pathFilterControl.setValue(path?.name ?? '', { emitEvent: false });
    this.filteredPaths = [...this.educationalPaths];
    this.onPathFilterChange();
  }

  clearPathFilter(): void {
    this.selectedPathId = null;
    this.pathFilterControl.setValue('', { emitEvent: true });
    this.onPathFilterChange();
  }

  getPathDisplayName(pathId: number | null): string {
    if (!pathId) return '';
    return this.educationalPaths.find((p) => p.path_id === pathId)?.name ?? '';
  }

  // ── Search ─────────────────────────────────────────────────────────────────

  onSearchInput(value: string): void {
    this.searchQuery = value;
    const hasName = value.trim().length >= 2;
    const hasPath = this.selectedPathId !== null;
    if (!hasName && !hasPath) {
      this.searchResults = [];
      this.searchPerformed = false;
      this.selection.clear();
      return;
    }
    this.searchSubject.next(value.trim());
  }

  private doSearch(): void {
    const name = this.searchQuery.trim();
    const hasName = name.length >= 2;
    const hasPath = this.selectedPathId !== null && this.selectedPathId > 0;

    if (!hasName && !hasPath) {
      this.searchResults = [];
      this.searchPerformed = false;
      return;
    }

    this.searching = true;
    this.searchPerformed = true;
    this.selection.clear();

    const params: Record<string, string> = {};
    if (hasName) params['name'] = name;
    if (hasPath) params['educational_path_id'] = String(this.selectedPathId);

    this.http
      .get<{ users: SearchUser[] }>(
        `${this.apiUrl}/events/instructor-invitations/search-users`,
        { params }
      )
      .subscribe({
        next: (res) => {
          const already = new Set(this.invitees.map((i) => i.user_id));
          this.searchResults = (res?.users ?? [])
            .filter((u) => !already.has(u.user_id))
            .map((u) => ({ ...u, name: this.displayName(u) }));
          this.searching = false;
        },
        error: (err) => {
          this.searching = false;
          this.searchResults = [];
          this.error = err?.error?.message || 'Søgning fejlede.';
        },
      });
  }

  // ── Selection helpers ──────────────────────────────────────────────────────

  isAllSelected(): boolean {
    return (
      this.searchResults.length > 0 &&
      this.selection.selected.length === this.searchResults.length
    );
  }

  isSomeSelected(): boolean {
    return this.selection.selected.length > 0 && !this.isAllSelected();
  }

  toggleAll(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.searchResults);
    }
  }

  toggleRow(user: SearchUser): void {
    this.selection.toggle(user);
  }

  get selectedCount(): number {
    return this.selection.selected.length;
  }

  addSelectedToInvitees(): void {
    const toAdd = this.selection.selected.filter(
      (u) => !this.invitees.some((i) => i.user_id === u.user_id)
    );
    this.invitees = [...this.invitees, ...toAdd];
    // Remove added users from search results
    const addedIds = new Set(toAdd.map((u) => u.user_id));
    this.searchResults = this.searchResults.filter(
      (u) => !addedIds.has(u.user_id)
    );
    this.selection.clear();
    this.error = null;
  }

  removeInvitee(user: SearchUser): void {
    this.invitees = this.invitees.filter((i) => i.user_id !== user.user_id);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  displayName(user: SearchUser): string {
    return (
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      user.email ||
      String(user.user_id)
    );
  }

  formatEventDate(ev: any): string {
    if (!ev?.start_date) return '';
    try {
      return new Date(ev.start_date).toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return ev.start_date;
    }
  }

  pathChipColor(index: number): string {
    const colors = [
      '#1565c0',
      '#2e7d32',
      '#6a1b9a',
      '#e65100',
      '#00695c',
      '#4527a0',
    ];
    return colors[index % colors.length];
  }

  // ── Send ───────────────────────────────────────────────────────────────────

  sendInvitation(): void {
    this.error = null;
    if (this.invitees.length === 0) {
      this.error = 'Tilføj mindst én inviteret.';
      return;
    }
    if (!this.responseDeadline) {
      this.error = 'Vælg en svarfrist.';
      return;
    }
    const deadlineIso = this.responseDeadline.toISOString();
    this.sending = true;
    this.http
      .post<{ guid: string }>(`${this.apiUrl}/events/instructor-invitations`, {
        event_guids: this.eventGuids,
        invitee_user_ids: this.invitees.map((i) => i.user_id),
        response_deadline: deadlineIso,
      })
      .subscribe({
        next: () => {
          this.sending = false;
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.sending = false;
          this.error = err?.error?.message || 'Kunne ikke oprette invitation.';
        },
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
